import {Component, EventEmitter, Input, Output} from '@angular/core';
import {
  faArrowRight,
  faCheck,
  faChevronDown,
  faChevronRight,
  faCircleExclamation,
  faClock,
  faFlagCheckered,
  faRotate,
  faShieldHalved
} from "@fortawesome/free-solid-svg-icons";
import {IFlow, IFlowByStages} from "../../../../../data/interfaces/iflow.interface";
import {
  archivosCargados,
  cantidadArchivos
} from "../../data/constants/documento-archivos.constant";
import {ESTADOS_LX, IEstadoLx} from "../../../../../shared/ui/liga-ui";
import {
  agruparPorSeccion,
  armarPasos,
  IPasoLista,
  IResumenPasos,
  ISeccionLista,
  RESUMEN_VACIO,
  unirTitulos
} from "./pasos-flujo";

// Lista de pasos del flujo: tarjeta «Siguiente paso» + todos los pasos en el
// orden del flujo, con el chip de estado homologado. Si getFlow manda 2 o más
// secciones con pasos, los pasos van en secciones plegables («Tu identidad»,
// «Tu capacidad de pago»…); si no, en una sola lista como siempre. Cómo se
// mapea cada grupo del backend a un estado y cómo se arman las secciones: ver
// pasos-flujo.ts. Estilos en _liga-ui.scss (.lx-*) y _liga-a.scss (.lx-a-*).
@Component({
  selector: 'app-verification-documents-list',
  templateUrl: './verification-documents-list.component.html',
  styleUrls: ['./verification-documents-list.component.scss']
})
export class VerificationDocumentsListComponent {
  @Output() onClick = new EventEmitter()

  readonly estados = ESTADOS_LX
  readonly iconos = {
    listo: faCheck,
    alerta: faCircleExclamation,
    chevron: faChevronRight,
    desplegar: faChevronDown,
    flecha: faArrowRight,
    reloj: faClock,
    meta: faFlagCheckered,
    seguro: faShieldHalved,
    // Paso tomado de una solicitud anterior (chip «Reemplazar»).
    reemplazar: faRotate
  }

  resumen: IResumenPasos = RESUMEN_VACIO
  // «"A" y "B"»: los opcionales que aún puede subir, cuando ya no le toca nada.
  sugerenciaOpcionales = ''
  // Vacío = lista de siempre (0 o 1 sección, o backend sin `sections`).
  secciones: ISeccionLista[] = []
  private datos!: IFlowByStages
  private abiertas = new Set<string>()
  // undefined = aún no se pinta ninguna vez.
  private claveAbiertaPorDefecto: string | null | undefined = undefined

  @Input()
  set data(valor: IFlowByStages) {
    this.datos = valor
    this.resumen = armarPasos(valor)
    this.sugerenciaOpcionales = unirTitulos(this.resumen.opcionalesPendientes.map(p => p.titulo))
    this.secciones = agruparPorSeccion(this.resumen.pasos, valor?.sections, this.resumen.siguiente)
    this.ajustarAbiertas()
  }

  get data(): IFlowByStages {
    return this.datos
  }

  // Mismas reglas de siempre: rechazados y pendientes habilitados emiten la
  // etapa tal cual llegó; bloqueados, listos y en revisión no hacen nada (salvo
  // un listo multiarchivo con rango y opcionales por subir: ver pasos-flujo).
  abrir(paso: IPasoLista | null) {
    if (paso && paso.abrible) this.onClick.emit(paso.etapa)
  }

  // Para el interior del renglón (ng-template, donde el contexto no lleva tipo).
  estadoDe(paso: IPasoLista): IEstadoLx {
    return ESTADOS_LX[paso.estado]
  }

  porId(_: number, paso: IPasoLista): string {
    return paso.id
  }

  porClave(_: number, seccion: ISeccionLista): string {
    return seccion.clave
  }

  estaAbierta(seccion: ISeccionLista): boolean {
    return this.abiertas.has(seccion.clave)
  }

  // Se pueden tener varias abiertas a la vez.
  alternar(seccion: ISeccionLista) {
    if (this.abiertas.has(seccion.clave)) this.abiertas.delete(seccion.clave)
    else this.abiertas.add(seccion.clave)
  }

  // Al pintar: solo la del siguiente paso. Si la lista se recarga y el siguiente
  // sigue en la misma sección, se respeta lo que el usuario abrió o cerró; si
  // cambió de sección, se vuelve a abrir solo la nueva.
  private ajustarAbiertas() {
    const porDefecto = this.secciones.find(s => s.abierta)?.clave ?? null
    if (porDefecto !== this.claveAbiertaPorDefecto) {
      this.abiertas = new Set(porDefecto ? [porDefecto] : [])
      this.claveAbiertaPorDefecto = porDefecto
      return
    }
    const claves = new Set(this.secciones.map(s => s.clave))
    this.abiertas.forEach(clave => {
      if (!claves.has(clave)) this.abiertas.delete(clave)
    })
  }

  // Pasos multiarchivo (p. ej. los N recibos de nómina): «k de N archivos».
  cantidadArchivos(stage: IFlow): number {
    return cantidadArchivos(stage)
  }

  archivosCargados(stage: IFlow): number {
    return archivosCargados(stage)
  }
}
